import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createLocationYuq } from 'utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { createLocation, getLocationDetail } from 'services/locations';
import { RoomInfo } from 'types/room';
import { renderTitle } from 'utils/renderTitleLocation';
type FormCreateLocation = {
  names: {
    value: string;
  }[];
};

export const useInjection = () => {
  const navigate = useNavigate();
  const { clientId, locationId, clientName } = useParams();
  const { setFlash } = useFlash();
  const [searchParams] = useSearchParams();
  const level = searchParams.get('level');
  const { state } = useLocation();
  const basePath = state?.path ? '/inspection' : '/clients';

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<FormCreateLocation>({ resolver: yupResolver(createLocationYuq) });

  const {
    fields: fieldNames,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'names' as never
  });

  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);
  const [title, setTitle] = useState<{
    title: string;
    textButton: string;
  }>({
    title: '',
    textButton: ''
  });

  const paths: RoomInfo = useMemo(() => {
    const pathInit = [
      {
        name: state?.path ? 'Inspection' : 'Clients',
        level: 999,
        onClick: () => navigate(basePath)
      },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`${basePath}/${clientName}/${clientId}/location`)
      }
    ];
    const locationInit = roomInfo.find((item) => item.id === Number(locationId));
    if (!locationInit)
      return [...pathInit, { name: title.textButton, level: 999, onClick: () => {} }];
    return [
      ...pathInit,
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          return {
            ...item,
            onClick: () => navigate(`${basePath}/${clientName}/${clientId}/location/${item.id}`)
          };
        }),
      { name: title.textButton, level: 999, onClick: () => {} }
    ];
  }, [clientId, clientName, locationId, navigate, roomInfo, title]);

  const handleGetInitLocations = useCallback(async () => {
    if (!locationId || !level) {
      setTitle(renderTitle(0));
      return;
    }
    const data = (await getLocationDetail(Number(locationId))) as {
      location: Location;
      room: RoomInfo;
    };
    setRoomInfo(data.room);
    setTitle(renderTitle(Number(level)));
  }, [locationId, level]);

  useEffect(() => {
    handleGetInitLocations();
  }, [handleGetInitLocations]);

  const handleCreateClient = useCallback(
    async (value: FormCreateLocation) => {
      const payload = {
        names: value.names.map((item) => item.value),
        clientId: Number(clientId) || undefined,
        parentId: Number(locationId) || undefined,
        level: Number(level) || undefined
      };
      await createLocation(payload);
      setFlash({ type: 'success', message: 'Create location successful' });

      const newPath = location.pathname.replace('/create', '');
      navigate(newPath);
    },
    [clientId, locationId, navigate, setFlash, level]
  );

  const { asyncCallback } = useAsyncCallback(handleCreateClient, []);
  return {
    backToClient: () => navigate(`${location.pathname.replace('/create', '')}`),
    handleSubmit,
    errors,
    onCreateLocation: asyncCallback,
    fieldNames,
    append,
    remove,
    control,
    paths,
    title
  };
};
